/**
 * Why a presentation type cannot be chosen right now, or `null` (CT-5183).
 *
 * One place for the three reasons the type grid dims a tile, so the tooltip
 * and the rule cannot drift apart. Everything here is derived from the
 * catalog's own columns, except `UNBUILDABLE_TYPES` below, which names the
 * gaps this builder still has.
 */

/**
 * The types the Data step cannot finish, and why (CT-5183, revised CT-5641).
 *
 * Measured type by type against dev; every other type in the catalog saves
 * and publishes from the builder. The reason travels with the code: the type
 * picker dims these and the tooltip says which sentence applies, so an author
 * reads the rule instead of discovering it at publish.
 *
 * **Three came off this list when the measure picker landed.** `COST_KPI`,
 * `SPEND_BREAKDOWN` and `CORRELATION` were dimmed because the Data step
 * emitted one `COUNT_ROWS` and nothing else; they were probed against dev on
 * 2026-10-20 and all three create, publish and execute once a measure can be
 * chosen. What `COST_KPI` and `SPEND_BREAKDOWN` actually need is an
 * operation whose **output** is `DECIMAL|NUMERIC|MONEY` — `AVG` qualifies
 * where `SUM` over an integer does not — and `CORRELATION` needs two
 * measures and a grouper, since two aggregates alone derive a `SCALAR` it
 * refuses. All three rules live in the type's own schema, so the editor
 * enforces them without a list here.
 *
 * `ACTIVITY` is deliberately absent. Its contract is reachable — it only
 * wants a date column, and no tenant entity has a business one yet. That is a
 * gap in the data, not in the builder.
 *
 * The keys are reason codes, resolved to a sentence by the picker's i18n.
 */
export const UNBUILDABLE_TYPES = Object.freeze({
  /*
    Not a measure gap: a composite metric is a **formula** over its
    components, and `create` refuses one without it —
    `UI_COMPOSITE_FORMULA_INVALID`, measured on dev 2026-10-20. Guide 17 §4
    is explicit that the governed rule that formula needs has no public
    authoring path yet, and guide 18 §6 says not to offer it as finished.
  */
  COMPOSITE_METRIC: 'formula',
  // `LIST` still needs a `LIST_VIEW` artifact and `mui_ui_artifacts` is still
  // empty (CT-5561). Guide 02 confirms rev4 does not create one for it.
  LIST: 'viewBinding',
  // Latitude and longitude, or a region code — fields these entities lack.
  MAP: 'geoFields',
});

/**
 * The result shapes the builder can hand a type, given the data mode.
 *
 * The Data step has exactly two levers, so the shapes it can produce are few:
 * a summary with no grouper is a single value and a grouped one is a
 * collection.
 *
 * A record set reaches **both** `COLLECTION` and `ONE`. The builder does not
 * choose between them and never did: it sends a record set, and the backend
 * derives the data source's `result_shape` from the type's own
 * `allowed_result_shapes` — a TABLE gets `COLLECTION`, a PROFILE_CARD gets
 * `ONE`, and the record is pinned at execution by the `CURRENT_RECORD_UUID`
 * context filter the backend authors (CT-5562, dev 2026-10-06).
 *
 * Listing only `COLLECTION` here is what dimmed PROFILE_CARD after its real
 * blocker was lifted: the guard outlived the limitation it described.
 */
const reachableShapes = (dataMode, supportsGrouping) => {
  if (dataMode === 'RECORD_SET') return ['COLLECTION', 'ONE'];
  return supportsGrouping ? ['SCALAR', 'COLLECTION'] : ['SCALAR'];
};

/**
 * An existing dataset has no levers: its shape is fixed by the query, so the
 * one shape it produces is the only one reachable (CT-5296). Without this a
 * single-value dataset left every chart enabled — a summary *could* group,
 * so `reachableShapes` offered a collection the dataset never returns.
 *
 * `single_value` and `grouped` remain for widgets saved while the picker
 * still offered shapes; `/dataset/list` publishes record sets and nothing
 * else (CT-5860).
 */
const DATASET_SHAPES = Object.freeze({
  single_value: ['SCALAR'],
  grouped: ['COLLECTION'],
  /*
    A published record set carries rows, and the widget's own bindings decide
    what becomes of them: counted or summed it is a `SCALAR`, grouped a
    `COLLECTION`. It used to say `COLLECTION` alone, which dimmed `KPI` and
    every chart — on a dataset that is exactly what the entity path builds,
    since that path publishes a record set too.

    `ONE` stays out. Narrowing to a single record needs a
    `CURRENT_RECORD_UUID` context filter, and a dataset has nowhere to carry
    one.
  */
  record_set: ['SCALAR', 'COLLECTION'],
});

/** The data mode an existing dataset implies — its shape, not an operation. */
export const datasetDataMode = (datasetShape) => (
  datasetShape === 'record_set' ? 'RECORD_SET' : 'SUMMARY'
);

/**
 * @param {object} type A row of the presentation-type catalog.
 * @param {string} dataMode `SUMMARY` or `RECORD_SET`, from the Data step.
 * @param {string} [datasetShape] `single_value` / `grouped` / `record_set` when
 *   the source is an existing dataset, whose shape is fixed; omitted on the
 *   entity path, where the author's operation and groupers decide it.
 * @returns {string|null} A reason code the picker resolves to a sentence.
 */
export function typeBlockedReason(type, dataMode, datasetShape) {
  /*
    No mode means the dataset-first path (CT-5725): the selection is a plain
    RECORD_SET dataset and the widget's own query is derived from its
    bindings, so the catalog — not the Data step — decides whether a type can
    aggregate it. Gating here would dim every summary type against a record
    set that is about to be summarised.
  */
  if (!dataMode) return UNBUILDABLE_TYPES[type.code] ?? null;

  /*
    On a record-set dataset the mode says nothing either (CT-5860).
    `allowed_data_modes` describes the query the *widget* runs, and on raw
    rows that query is whatever its bindings make it — the same reason the
    entity path has no mode to gate on. Keeping the check here dimmed every
    summary type against rows that were about to be summarised.
  */
  const bindingsDecide = datasetShape === 'record_set';

  // A type that declares no mode is left alone: the backend treats an
  // undeclared capability as permitted, and refusing one on a guess is worse
  // than offering one the backend might refuse.
  if (!bindingsDecide && type.allowedDataModes?.length
    && !type.allowedDataModes.includes(dataMode)) {
    // An existing dataset has no Data-step mode to switch — "switch to
    // Summarize / Record set" would be advice the author cannot follow. The
    // only way out there is another dataset, so that is what it says.
    if (DATASET_SHAPES[datasetShape]) {
      return dataMode === 'RECORD_SET' ? 'datasetModeRecordSet' : 'datasetModeSummary';
    }
    return dataMode === 'RECORD_SET' ? 'modeRecordSet' : 'modeSummary';
  }

  // The gaps this builder is known to have, measured type by type.
  if (UNBUILDABLE_TYPES[type.code]) return UNBUILDABLE_TYPES[type.code];

  // The mode is allowed and the contract is fillable, and it still cannot be
  // published: the shape this mode produces is not one the type accepts.
  //
  // `AI_SUMMARY` and `METRIC_ROW` are both declared for `SUMMARY`, both refuse
  // grouping, and neither accepts `SCALAR` — so a summary can only hand them a
  // single value they reject (`WIDGET_PRESENTATION_RESULT_SHAPE_MISMATCH`),
  // and the one way out of a single value is the grouping they forbid. Whether
  // that is a mistake in `allowed_data_modes`, in `allowed_result_shapes` or in
  // `supports_grouping` is the backend's to settle; derived here, this guard
  // disappears on its own the day the catalog changes.
  const allowed = type.allowedResultShapes ?? [];
  if (!allowed.length) return null;
  const fixed = DATASET_SHAPES[datasetShape];
  const reachable = fixed ?? reachableShapes(dataMode, type.supportsGrouping);
  if (reachable.some((shape) => allowed.includes(shape))) return null;

  if (fixed) return datasetShape === 'single_value' ? 'shapeDatasetSingle' : 'shapeDatasetCollection';
  return dataMode === 'RECORD_SET' ? 'shapeRecordSet' : 'shapeSummary';
}

export default typeBlockedReason;
